// [목적] ChatPage/ChatMobile 공용 대화 상태 머신 훅 — RAG 대화(turn 반복) + 슬롯 제출 + 생성 폴링을 한 곳에서 관리
// [이유] Spec(docs/specs/Approved/2026-07-05_fe-rag-generate-rewire.md) §7 Resolved Decisions(2026-07-19):
//        죽은 /plan API(usePlanSession/usePlanJob/usePlanGenerateStream/usePlanSlotFilling) 대신 실존하는
//        /rag API 로 재배선한다. 흐름 매핑(D-3/D-4 확정):
//          1) 첫 발화 → createConversation(utterance)
//          2) 응답 type 별: ask/final → bot 버블, clarify → clarifyMeta.candidates 를 칩으로,
//             ready → confirm 버블("폴리오 만들어드릴까요?")
//          3) 이후 입력은 turn(convId, utterance) 반복
//          4) confirm 수락 → submitSlots(convId, {}) → useFolioGeneration 폴링 → done: PDF 카드, failed: 재시도
//          5) 400 rag.SLOTS_INCOMPLETE → missingSlots 안내 봇 버블(이례적 방어 경로)
//        D-1: 새로고침 복구 없음(?sid 재도입 안 함) — 이 훅은 항상 convId=null 로 시작.
//        D-2: 생성 취소는 폴링 중단만(cancel API 없음) — cancelled 플래그로 useFolioGeneration 비활성화.
//        ChatPage(데스크톱)/ChatMobile 이 각자의 말풍선 JSX 만 소유하고, 네트워크·상태 로직은 이 훅에 위임해
//        두 페이지 간 중복(구 파일에서 반복되던 send/handleChipAnswer/handleComplete 로직 쌍둥이)을 제거한다.
// [사이드 이팩트] rag.client.ts 의 createConversation/turn/submitSlots 호출 + useFolioGeneration 폴링(2초 간격).
//                submitSlots 성공 시 queryKeys.rag.generation(convId) 쿼리를 invalidate 하여 실패 후 재시도 시
//                (terminal 상태로 멈춰있던 폴링을) 즉시 재개시킨다.
// [수정 시 고려사항] clarify 칩 클릭/자유 텍스트 입력 모두 turn() 으로 수렴 — BE 는 이 둘을 구분하지 않는다.
//                   generation.status 가 done/failed 로 전이할 때 메시지를 1회만 append 하기 위해
//                   announcedTerminal 로 already-announced 여부를 추적한다(폴링 재렌더마다 중복 append 방지).
//                   [G1-2, 2026-09-17] convId 만료(404 rag.SESSION_NOT_FOUND/410 rag.SESSION_EXPIRED) 복구
//                   경로 추가 — session-expired 버블(text=실패 직전 원문) + startNewConversation(). 새로고침
//                   없이 새 세션 시작 + 마지막 입력 복원(호출부 ChatPage/ChatMobile 이 입력창에 복원).
//                   [FlowLine 83963a6b, 2026-09-18] sendConceptChip() 추가 — 추천 컨셉 칩(useConcepts) 클릭 시
//                   samplePrompt 를 기존 send() 로 전송하는 동시에 concept 선택값을 selectedConcept 로 기억해둔다.
//                   POST /:id/slots(SlotSubmitSchema, apps/api slot-submit.schema.ts)는 하드필수 5종이 모두
//                   채워져야만 통과·저장되므로(미충족 시 rag.SLOTS_INCOMPLETE 로 throw, merge 결과 미저장) 칩
//                   클릭 시점(대화 시작 전, 하드필수 슬롯 미확보)에 concept 만 즉시 submitSlots 하면 항상 실패해
//                   아무것도 저장되지 않는다(ponytail — 실패가 확정적인 호출은 추가하지 않음) — 따라서
//                   selectedConcept 는 로컬 상태로만 들고 있다가, 하드필수 슬롯이 모두 채워진 뒤 호출되는
//                   confirmGenerate() 의 최종 submitSlots 바디에 병합해 함께 제출한다.

import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConceptId } from '@gangwoncanvas/types';

import { ApiError } from '../api/http.js';
import { queryKeys } from '../api/queryKeys.js';
import {
  createConversation,
  turn,
  submitSlots,
  SlotsIncompleteDetailsSchema,
  type CreateConversationResponse,
  type TurnResponse,
} from '../api/rag.client.js';
import { generationErrorMessage } from '../utils/generationErrorMessage.js';
import { useFolioGeneration } from './useFolioGeneration.js';

// ── 타입 ────────────────────────────────────────────────────────────────────

/**
 * [목적] clarify 칩 1건 — BE ClarifyMeta.candidates 원소를 그대로 노출(label 표시, value 는 turn() 전송용)
 * [이유] zod z.unknown() 필드는 undefined 를 허용하므로 zod 추론 타입에서 value 가 옵셔널로 나온다
 *        (rag.client.ts ClarifyMetaSchema.candidates[].value: z.unknown()) — 이 타입도 동일하게 옵셔널.
 * [사이드 이팩트] 없음(타입 정의).
 * [수정 시 고려사항] rag.client.ts ClarifyMetaSchema 변경 시 이 인터페이스도 동기 갱신.
 */
export interface ClarifyOption {
  label: string;
  value?: unknown;
}

/**
 * [목적] 대화 메시지 버블 1건 — 두 페이지가 공유하는 최소 표시 모델
 * [이유] role 로 렌더 분기(me/bot=일반 버블, clarify=칩 버블, confirm=생성 확인 버블,
 *        pdf-ready=PDF 다운로드 카드, pdf-failed=재시도 카드, session-expired=세션 만료 복구 카드).
 * [사이드 이팩트] 없음(순수 상태 모델).
 * [수정 시 고려사항] 구 Msg(SlotFillingState 기반 followup/knowledge/refusal 등)는 신 계약에 대응 필드가 없어
 *                   전부 제거됨(Spec §7 "구 응답 필드에 묶인 로직은 신 계약으로 단순화").
 */
export interface ChatMsg {
  role: 'me' | 'bot' | 'clarify' | 'confirm' | 'pdf-ready' | 'pdf-failed' | 'session-expired';
  /**
   * role='session-expired' 일 때는 실패 직전 사용자가 보낸 원문(있으면) — "새 대화 시작" 클릭 시
   * 입력창에 복원해 재전송을 돕는다(G1-2). 없으면 빈 문자열.
   */
  text: string;
  /** role='clarify' 일 때만 사용 */
  clarifyOptions?: ClarifyOption[];
  /**
   * [목적] role='pdf-ready' 일 때만 사용 — 영속된 trip_portfolios.id(G1-1)
   * [이유] "폴리오 보기"(/folio/:id) CTA 를 렌더하려면 done 전이 시점의 portfolioId 값을 버블에 함께
   *        고정해야 한다(이후 폴링이 계속되며 generation 상태가 바뀌어도 버블은 불변).
   * [사이드 이팩트] 없음.
   * [수정 시 고려사항] BE 영속 실패로 null 이면 CTA 미노출(호출부가 방어적으로 분기).
   */
  portfolioId?: string | null;
}

const GREETING =
  '안녕하세요! 어떤 강원 여행을 그려볼까요?\n동반자, 일정, 무드를 자유롭게 말해주세요 ✦';

/**
 * [목적] 세션 만료/미발견 에러 판별 — rag.SESSION_NOT_FOUND(404)/rag.SESSION_EXPIRED(410)
 * [이유] G1-2: convId 가 BE Redis 에서 만료·소실된 경우(TTL 경과, 서버 재시작 등) turn/submitSlots 호출이
 *        이 두 코드로 실패한다 — 지금까지는 일반 "일시적인 오류" 문구로만 처리되어 사용자가 무한히
 *        같은 텍스트를 재전송해도 계속 실패하는 막다른 경로였다(BE codes.ts 주석: "새 대화를 시작해 주세요").
 * [사이드 이팩트] 없음(순수 판별 함수).
 * [수정 시 고려사항] BE 가 세션 관련 에러 코드를 추가하면 이 집합에도 추가해야 복구 경로가 걸린다.
 */
function isSessionExpiredError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.code === 'rag.SESSION_NOT_FOUND' || err.code === 'rag.SESSION_EXPIRED')
  );
}

/**
 * [목적] 입력 가드레일 차단·길이 초과를 일반 오류와 구분해 사용자에게 안내할 문구 반환(해당 없으면 null)
 * [이유] 2026-09-18 실측: 되묻기 답변이 가드레일에 차단되면 "일시적인 오류가 발생했어요. 다시 말씀해주세요."
 *        가 떴다. 차단은 일시적 오류가 아니고, 같은 말을 반복하면 또 차단되므로 사용자가 막다른 길에 갇힌다
 *        (세션 만료 건과 동일한 유형의 UX 결함 — 위 isSessionExpiredError 주석 참조).
 * [사이드 이팩트] 없음(순수 판별 함수).
 * [수정 시 고려사항] 차단 **사유(category/reason)는 BE 가 응답에 담지 않는다**(우회 힌트 방지 — BE codes.ts
 *                   주석). 따라서 문구도 사유를 특정하지 말고 "무엇을 하면 되는지"만 안내한다.
 */
export function guardrailBlockMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'rag.INPUT_TOO_LONG') {
    return '입력이 너무 길어요. 조금 더 짧게 말씀해 주세요.';
  }
  if (err.code === 'rag.INPUT_BLOCKED') {
    return '이 내용은 처리할 수 없어요. 강원 여행 계획과 관련된 내용으로 말씀해 주세요.';
  }
  return null;
}

// ── 응답 → 메시지 변환 ────────────────────────────────────────────────────────

/**
 * [목적] createConversation/turn 응답(type/reply/clarifyMeta)을 ChatMsg 배열로 변환
 * [이유] BE OQ-6 매핑: ask/final→텍스트 버블, clarify→칩 버블, ready→생성 확인 버블.
 *        createConversation·turn 두 함수의 응답 스키마가 동일 구조(convId 유무만 차이)라 공유 가능.
 * [사이드 이팩트] 없음(순수 변환 함수).
 * [수정 시 고려사항] type='ready' 인데 reply=null 인 경우 기본 안내 문구로 대체(BE 가 항상 채워주지만 방어적 처리).
 *                   notice(확정 슬롯 잠금 안내)가 있으면 본 응답 앞에 봇 말풍선 한 줄로 붙인다 —
 *                   createConversation 응답에는 notice 가 없으므로 'in' 으로 좁혀 읽는다.
 */
/**
 * [목적] 메시지 목록 뒤에 새 버블을 붙이되, 새 버블에 confirm 이 있으면 이전 confirm 버블을 걷어낸다
 * [이유] QA P2-06: 'ready' 응답이 여러 턴 오거나(다듬기 후 재입력), 취소 후 재확인 버블이 추가될 때마다
 *        "폴리오 생성 확인" 그룹이 위로 쌓였고, 생성 실패 후 busy 가 풀리면 지난 버블의 [네, 만들어주세요]
 *        버튼이 다시 활성화돼 어느 것을 눌러야 하는지 알 수 없었다. 확인 버블은 항상 최신 1개만 유효하다.
 * [사이드 이팩트] 없음(순수 함수). confirm 이 없는 버블 추가에는 기존 동작(단순 append)과 동일.
 * [수정 시 고려사항] 확인 버블 role 을 추가/변경하면 이 함수와 STALE_ON_RETRY_ROLES 를 함께 갱신할 것.
 */
function appendMessages(prev: ChatMsg[], next: ChatMsg[]): ChatMsg[] {
  return next.some((msg) => msg.role === 'confirm')
    ? [...prev.filter((msg) => msg.role !== 'confirm'), ...next]
    : [...prev, ...next];
}

/** 생성 시작(재시도 포함)·거절 시 걷어낼 "이전 결정 대기" 버블 — 확인 버블과 실패 재시도 버블 */
const STALE_ON_RETRY_ROLES: ReadonlyArray<ChatMsg['role']> = ['confirm', 'pdf-failed'];

function responseToMessages(response: CreateConversationResponse | TurnResponse): ChatMsg[] {
  const notice = 'notice' in response ? response.notice : undefined;
  const body = responseBodyToMessages(response);
  return notice ? [{ role: 'bot', text: notice }, ...body] : body;
}

function responseBodyToMessages(response: CreateConversationResponse | TurnResponse): ChatMsg[] {
  switch (response.type) {
    case 'ask':
    case 'final':
      return response.reply ? [{ role: 'bot', text: response.reply }] : [];
    case 'clarify':
      return [
        {
          role: 'clarify',
          text: response.reply ?? '',
          clarifyOptions: response.clarifyMeta?.candidates ?? [],
        },
      ];
    case 'ready':
      return [
        { role: 'bot', text: response.reply ?? '여행 정보가 충분히 모였어요 ✦' },
        { role: 'confirm', text: '폴리오를 만들어드릴까요?' },
      ];
  }
}

// ── 훅 ────────────────────────────────────────────────────────────────────

export interface UseChatFlowResult {
  msgs: ChatMsg[];
  /** 'chat' = 대화 중(턴 반복), 'generating' = submitSlots 성공 후 폴링 중 */
  phase: 'chat' | 'generating';
  /** turn/createConversation 응답 대기 중(중복 전송 방지) */
  sending: boolean;
  convId: string | null;
  generation: ReturnType<typeof useFolioGeneration>;
  /** 자유 텍스트 전송 — 첫 호출이면 createConversation, 이후는 turn */
  send: (text: string) => Promise<void>;
  /**
   * [목적] 추천 컨셉 칩 클릭 — samplePrompt 를 send() 로 전송 + conceptId 를 selectedConcept 로 기억
   * [이유] 기존 send() 흐름(첫 발화=createConversation, 이후=turn)은 그대로 유지하면서, 이후
   *        confirmGenerate() 최종 제출 시 concept 슬롯을 함께 실어 보내기 위한 진입점.
   */
  sendConceptChip: (samplePrompt: string, conceptId: ConceptId) => Promise<void>;
  /** clarify 칩 클릭 — label 은 me 버블 표시용, value 는 turn() 전송값(문자열화) */
  answerClarify: (label: string, value: unknown) => Promise<void>;
  /** confirm 버블 "네, 만들어주세요" 또는 재시도 — submitSlots 호출 */
  confirmGenerate: () => Promise<void>;
  /** confirm 버블 "조금 더 다듬을래요" — API 호출 없이 자유 입력 유도 메시지만 추가 */
  declineGenerate: () => void;
  /** 생성 중 취소 — 폴링만 중단(D-2, cancel API 없음) */
  cancelGeneration: () => void;
  /**
   * [목적] 세션 만료(session-expired 버블) 이후 "새 대화 시작" — convId/msgs/phase 를 초기 상태로 리셋
   * [이유] G1-2: convId 가 BE 에서 소실되면 같은 convId 로는 계속 실패하므로 완전히 새 세션으로
   *        시작해야 한다(D-1 새로고침과 동일 효과를 버튼 클릭만으로 제공).
   */
  startNewConversation: () => void;
}

/**
 * [목적] RAG 대화 전체 상태 머신 — ChatPage/ChatMobile 이 공유하는 유일한 로직 소유자
 * [이유] Spec §7 구현 스코프: "ChatPage/ChatMobile 의 plan.client 호출을 rag API 로 교체" —
 *        두 페이지가 동일 흐름을 각자 구현하면 구 파일처럼 로직이 두 벌로 갈라져 드리프트 위험이 크다.
 * [사이드 이팩트] rag.client 함수 호출 + useFolioGeneration 폴링 — 아래 각 핸들러 주석 참조.
 * [수정 시 고려사항] 페이지 컴포넌트는 이 훅의 반환값만으로 렌더해야 한다(별도 네트워크 호출 추가 금지).
 */
export function useChatFlow(): UseChatFlowResult {
  const queryClient = useQueryClient();

  const [msgs, setMsgs] = useState<ChatMsg[]>([{ role: 'bot', text: GREETING }]);
  const [convId, setConvId] = useState<string | null>(null);
  const [phase, setPhase] = useState<'chat' | 'generating'>('chat');
  const [sending, setSending] = useState(false);
  // [목적] D-2: 취소 시 폴링만 멈추는 플래그 — cancel API 가 없으므로 FE 단독으로 폴링 훅을 비활성화
  const [cancelled, setCancelled] = useState(false);
  // [목적] generation.status done/failed 전이를 1회만 메시지로 announce 하기 위한 추적 상태
  const [announcedTerminal, setAnnouncedTerminal] = useState<'done' | 'failed' | null>(null);
  // [목적] 추천 컨셉 칩 클릭으로 선택된 concept — confirmGenerate() 최종 submitSlots 제출까지 보관
  // [이유] 위 헤더 주석 참조: 칩 클릭 시점엔 하드필수 슬롯이 없어 즉시 제출이 불가능하므로 로컬 상태로 유지.
  const [selectedConcept, setSelectedConcept] = useState<ConceptId | null>(null);

  const generation = useFolioGeneration(
    phase === 'generating' && !cancelled ? (convId ?? undefined) : undefined,
  );

  // [목적] generation.status 가 done/failed 로 전이하면 해당 메시지를 1회 append 하고 phase 를 'chat' 으로 되돌림
  // [이유] AC: "done: PDF 다운로드 카드, failed: 재시도 CTA" — 폴링은 2초마다 재렌더를 유발하므로
  //        announcedTerminal 가드 없이는 상태가 유지되는 동안 매 렌더마다 메시지가 중복 추가된다.
  //        phase 를 'chat' 으로 되돌려 입력창을 다시 활성화한다(사용자가 done 이후에도 계속 대화 가능,
  //        failed 이후에도 재시도 버튼(confirmGenerate)이 phase 를 다시 'generating' 으로 전환하므로 무해).
  // [사이드 이팩트] setMsgs 로 pdf-ready/pdf-failed 버블 append + setPhase('chat').
  //                phase가 'chat'으로 바뀌면 useFolioGeneration 의 convId 인자가 undefined 로 바뀌어 쿼리가
  //                비활성화되지만, react-query 는 동일 queryKey 의 마지막 data 를 유지하므로 이 렌더 사이클에서
  //                즉시 'idle' 로 되돌아가지 않는다(이미 메시지를 append 한 뒤이므로 문제 없음).
  // [수정 시 고려사항] confirmGenerate() 가 재시도 시 announcedTerminal 을 null 로 리셋해야 재-announce 가능.
  useEffect(() => {
    if (generation.status === 'done' && announcedTerminal !== 'done') {
      setAnnouncedTerminal('done');
      setMsgs((m) => [...m, { role: 'pdf-ready', text: '', portfolioId: generation.portfolioId }]);
      setPhase('chat');
    } else if (generation.status === 'failed' && announcedTerminal !== 'failed') {
      setAnnouncedTerminal('failed');
      // [수정: QA-P1-02] BE 내부 rag.* 코드를 그대로 노출하지 않고 한글 문구로 매핑(generationErrorMessage) +
      // 재시도를 거듭할 때마다 이전 pdf-failed 버블이 쌓이지 않도록 새로 추가하기 전에 기존 것을 제거한다
      // (재시도 후 화면에는 항상 가장 최근 실패 메시지 1개만 남는다).
      setMsgs((m) => [
        ...m.filter((msg) => msg.role !== 'pdf-failed'),
        { role: 'pdf-failed', text: generationErrorMessage(generation.errorCode) },
      ]);
      setPhase('chat');
    }
  }, [generation.status, generation.errorCode, generation.portfolioId, announcedTerminal]);

  /**
   * [목적] 자유 텍스트 발화 전송 — convId 없으면 createConversation, 있으면 turn
   * [이유] Spec 흐름 매핑 1)/3): 첫 발화=createConversation, 이후 반복=turn.
   * [사이드 이팩트] 성공 시 convId 최초 설정(1회) + 응답을 ChatMsg 로 변환해 append.
   *                실패 시 일반 오류 안내 버블만 추가(SLOTS_INCOMPLETE 는 이 경로에서 발생하지 않음 — turn/create 는
   *                다른 예외 카탈로그 사용).
   * [수정 시 고려사항] sending/phase==='generating' 중에는 호출 무시(중복 전송·생성 중 텍스트 전송 방지).
   */
  const send = useCallback(
    async (text: string): Promise<void> => {
      const t = text.trim();
      if (!t || sending || phase === 'generating') return;

      setMsgs((m) => [...m, { role: 'me', text: t }]);
      setSending(true);
      try {
        // [목적] convId 유무로 분기 — createConversation 응답에서만 convId 를 얻어 1회 저장
        // [이유] 삼항 표현식으로 response 타입을 좁히면 TurnResponse|CreateConversationResponse 유니온에서
        //        'convId' in response 내로잉이 zod z.unknown() 필드 추론과 얽혀 타입이 정확히 좁혀지지 않는다
        //        (tsc 에러 재현) — if/else 로 분기해 각 브랜치에서 정확한 타입을 직접 다루는 편이 더 단순하다(ponytail).
        // [사이드 이팩트] 없음 — 위 로직과 동일한 동작, 타입만 명확화.
        let response;
        if (convId) {
          response = await turn(convId, t);
        } else {
          const created = await createConversation(t);
          setConvId(created.convId);
          response = created;
        }
        setMsgs((m) => appendMessages(m, responseToMessages(response)));
      } catch (err) {
        // [목적] G1-2: 세션 만료(404/410)면 일반 오류 문구 대신 session-expired 버블(복구 경로) 추가
        // [이유] convId 가 BE 에서 소실된 경우 같은 convId 로 재전송해도 계속 실패한다 — text(t)를
        //        버블에 실어 "새 대화 시작" 클릭 시 입력창에 복원(마지막 입력 유실 방지).
        const blockMsg = guardrailBlockMessage(err);
        if (isSessionExpiredError(err)) {
          setMsgs((m) => [...m, { role: 'session-expired', text: t }]);
        } else if (blockMsg !== null) {
          setMsgs((m) => [...m, { role: 'bot', text: blockMsg }]);
        } else {
          setMsgs((m) => [
            ...m,
            { role: 'bot', text: '일시적인 오류가 발생했어요. 다시 말씀해주세요.' },
          ]);
        }
      } finally {
        setSending(false);
      }
    },
    [convId, sending, phase],
  );

  /**
   * [목적] 추천 컨셉 칩 클릭 — send(samplePrompt) 재사용 + conceptId 를 selectedConcept 로 기록
   * [이유] send() 를 그대로 재사용해 기존 첫 발화/이후 턴 분기 로직을 중복 구현하지 않는다(ponytail).
   *        conceptId 기록은 이후 confirmGenerate() 가 소비.
   * [사이드 이팩트] send() 와 동일한 네트워크 경로 + setSelectedConcept 로컬 상태 갱신.
   * [수정 시 고려사항] 같은 대화에서 칩을 여러 번 클릭하면 마지막 선택값으로 덮어써진다(다중 선택 미지원 — BE
   *                   concept 필드가 단일 enum 이므로 UX 상 자연스러운 제약).
   */
  const sendConceptChip = useCallback(
    async (samplePrompt: string, conceptId: ConceptId): Promise<void> => {
      setSelectedConcept(conceptId);
      await send(samplePrompt);
    },
    [send],
  );

  /**
   * [목적] clarify 칩 클릭 응답 — 칩 라벨을 me 버블로 보이고, value 를 문자열화해 turn() 전송
   * [이유] Spec 흐름 매핑 2): "선택값을 다음 turn utterance로 전송(options[].value 문자열화)".
   * [사이드 이팩트] send() 와 동일한 네트워크 경로(turn) — convId 는 clarify 도달 시점에 항상 존재.
   * [수정 시 고려사항] convId 없으면(방어적, 이론상 도달 불가) 조기 반환.
   */
  const answerClarify = useCallback(
    async (label: string, value: unknown): Promise<void> => {
      if (!convId || sending || phase === 'generating') return;

      setMsgs((m) => [...m, { role: 'me', text: label }]);
      setSending(true);
      try {
        const response = await turn(convId, String(value));
        setMsgs((m) => appendMessages(m, responseToMessages(response)));
      } catch (err) {
        // G1-2: 세션 만료 시 session-expired 버블(label 을 복원 텍스트로 사용) — send() 와 동일 사유.
        const blockMsg = guardrailBlockMessage(err);
        if (isSessionExpiredError(err)) {
          setMsgs((m) => [...m, { role: 'session-expired', text: label }]);
        } else if (blockMsg !== null) {
          setMsgs((m) => [...m, { role: 'bot', text: blockMsg }]);
        } else {
          setMsgs((m) => [
            ...m,
            { role: 'bot', text: '일시적인 오류가 발생했어요. 다시 말씀해주세요.' },
          ]);
        }
      } finally {
        setSending(false);
      }
    },
    [convId, sending, phase],
  );

  /**
   * [목적] confirm 버블 수락 또는 재시도 — POST /:id/slots(빈 바디) 호출 후 generating 전이
   * [이유] Spec 흐름 매핑 4): "확인 시 submitSlots(convId, {})" — 누적 슬롯이 이미 완성 상태이므로 빈 바디 제출.
   *        failed 이후 재시도도 동일 호출(멱등, D-2 근거: "잡은 짧고 멱등, 결과 무해").
   * [사이드 이팩트] 성공(202) 시 phase='generating' 전이 + announcedTerminal 리셋 + cancelled 해제 +
   *                queryKeys.rag.generation(convId) 캐시 제거(removeQueries, QA-P1-02 — 실패 후 재시도 시
   *                멈춰있던 폴링을 빈 상태부터 재개시켜 이전 실패의 stale 캐시를 새 결과로 오인하지 않게 함).
   *                실패(400 rag.SLOTS_INCOMPLETE) 시 missingSlots 안내 버블(이례적 방어 경로, AC).
   * [수정 시 고려사항] convId 없으면 조기 반환(confirm 버블까지 도달했으면 항상 존재).
   *                   [FlowLine 83963a6b] selectedConcept 이 있으면 바디에 concept 필드를 함께 실어 보낸다
   *                   (sendConceptChip() 이 기록한 값 — 대화 중 컨셉 칩을 클릭하지 않았으면 undefined 로 생략).
   */
  const confirmGenerate = useCallback(async (): Promise<void> => {
    if (!convId) return;

    try {
      await submitSlots(convId, selectedConcept ? { concept: selectedConcept } : {});
      setAnnouncedTerminal(null);
      setCancelled(false);
      // [수정: QA-P1-02] 재시도 시 이전 실패의 캐시된 generation 쿼리 데이터가 남아있으면, 폴링이 다시
      // 활성화되는 바로 그 렌더에서 react-query 가 그 캐시(stale 'failed')를 동기적으로 즉시 반환한다 —
      // 이 훅의 종료-상태 effect 가 announcedTerminal=null 과 맞물려 그 캐시를 "새 결과"로 오인해 즉시
      // 재-append 하고 phase 를 'chat' 으로 되돌려버려, 진짜 새 폴링 응답(성공/다른 실패)이 도착했을 땐
      // 이미 옵저버가 떨어져나가 화면에 반영되지 않는 경쟁 상태가 있었다(재시도가 실제로는 무시됨).
      // invalidate 대신 removeQueries 로 캐시 자체를 비워, 재활성화 시 폴링이 완전히 빈 상태(pending)에서
      // 시작하도록 한다.
      queryClient.removeQueries({ queryKey: queryKeys.rag.generation(convId) });
      // [수정: QA-P2-06] 생성이 시작되면 이전 confirm/실패 재시도 버블은 이미 처리된 결정이라 걷어낸다 —
      // 남겨 두면 재시도가 또 실패했을 때 옛 버튼이 다시 활성화돼 confirm 그룹이 여러 개 보인다.
      setMsgs((m) => m.filter((msg) => !STALE_ON_RETRY_ROLES.includes(msg.role)));
      setPhase('generating');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'rag.SLOTS_INCOMPLETE') {
        const parsed = SlotsIncompleteDetailsSchema.safeParse(err.details);
        const missing = parsed.success ? parsed.data.missingSlots.join(', ') : '';
        setMsgs((m) => [
          ...m,
          {
            role: 'bot',
            text: missing
              ? `아직 몇 가지 정보가 더 필요해요: ${missing}`
              : '아직 정보가 조금 더 필요해요.',
          },
        ]);
      } else if (isSessionExpiredError(err)) {
        // G1-2: 세션 만료 — 이 경로는 버튼 클릭(자유 텍스트 아님)이라 복원할 원문이 없어 text=''.
        setMsgs((m) => [...m, { role: 'session-expired', text: '' }]);
      } else {
        setMsgs((m) => [
          ...m,
          { role: 'bot', text: '생성을 시작할 수 없어요. 잠시 후 다시 시도해주세요.' },
        ]);
      }
    }
  }, [convId, queryClient, selectedConcept]);

  /**
   * [목적] confirm 버블 "조금 더 다듬을래요" — submitSlots 를 호출하지 않고 자유 입력을 유도
   * [이유] 구 ADR-CSFU6(선택 슬롯 되묻기)는 신 계약에 대응 슬롯 필드가 없어 단순화됨(Spec §7).
   *        "다듬을래요" 는 이제 API 호출 없이 사용자가 다시 자유 텍스트로 보완하도록 안내만 한다.
   * [사이드 이팩트] 없음(로컬 봇 버블 추가만).
   * [수정 시 고려사항] 이후 사용자가 입력하는 자유 텍스트는 send() 로 처리되어 turn() 이 호출된다(정상 경로 재사용).
   */
  const declineGenerate = useCallback((): void => {
    // [수정: QA-P2-06] 거절한 확인 버블은 걷어낸다(다시 'ready' 가 오면 새 버블이 붙는다).
    setMsgs((m) => [
      ...m.filter((msg) => msg.role !== 'confirm'),
      { role: 'bot', text: '알겠어요! 더 알려주시면 반영할게요 ✦' },
    ]);
  }, []);

  /**
   * [목적] 생성 중 취소 — 폴링만 중단(D-2, cancel API 없음)
   * [이유] Spec D-2: "FE 폴링 중단만(cancel API 불요)". 서버 잡은 계속 진행되나 결과를 조회하지 않을 뿐이므로 무해.
   * [사이드 이팩트] cancelled=true → useFolioGeneration convId=undefined 전달 → 쿼리 비활성화.
   *                phase 를 'chat' 으로 되돌리고 재시도 유도 confirm 버블을 추가.
   * [수정 시 고려사항] 재시도(confirm 버블 재클릭)는 confirmGenerate 가 cancelled=false 로 되돌린다.
   */
  const cancelGeneration = useCallback((): void => {
    setCancelled(true);
    setPhase('chat');
    setMsgs((m) =>
      appendMessages(m, [{ role: 'confirm', text: '생성을 취소했어요. 다시 만들어드릴까요?' }]),
    );
  }, []);

  /**
   * [목적] session-expired 버블 "새 대화 시작" 클릭 핸들러 — 전체 상태를 초기 마운트 상태로 리셋
   * [이유] G1-2: convId 가 BE 에서 소실된 세션은 복구 불가 — D-1(새로고침=새 대화)과 동일 효과를
   *        새로고침 없이 제공한다. 복원할 원문은 호출부(ChatPage/ChatMobile)가 msg.text 로 이미
   *        읽어 입력창에 넣어주므로 이 함수는 훅 내부 상태만 리셋한다.
   * [사이드 이팩트] 없음(네트워크 호출 없음) — convId=null 이므로 다음 send() 는 createConversation 부터 재시작.
   * [수정 시 고려사항] 폴링(useFolioGeneration)은 convId 가 null 이 되면 자동으로 enabled=false 전환됨.
   */
  const startNewConversation = useCallback((): void => {
    setConvId(null);
    setPhase('chat');
    setSending(false);
    setCancelled(false);
    setAnnouncedTerminal(null);
    setSelectedConcept(null);
    setMsgs([{ role: 'bot', text: GREETING }]);
  }, []);

  return {
    msgs,
    phase,
    sending,
    convId,
    generation,
    send,
    sendConceptChip,
    answerClarify,
    confirmGenerate,
    declineGenerate,
    cancelGeneration,
    startNewConversation,
  };
}
